import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { filter, Observable, shareReplay, switchMap } from 'rxjs';
import { OktaAuthStateService } from '@okta/okta-angular';
import { AuthState } from '@okta/okta-auth-js';

export const PRINCIPAL_RESOURCE_URL = '/api/internal/v1/principal';
export const LOGOUT_URL = '/logout';

export interface IIdentity {
  /**
   * The Okta login (username) for the end user.
   */
  username?: string;
  isAuthenticated: boolean;
  givenName?: string;
  surname?: string;
  surrogateUserId?: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface IPermission {}

/**
 * Represents the user identity and security claims
 *
 * @remarks
 * This interface is loosely based on analogous classes that exist in Spring Security and .NET core.   The purpose
 * of this class it to track user identity and security claims only.  It is not intended for user preferences,
 * profile information or other information that would be needed outside the security and identity domain.
 */
export interface IPrincipal {
  identity: IIdentity;
  permissions?: IPermission;
  // claims will be added here when needed.
}

@Injectable({
  providedIn: 'root',
})
export class AuthorizationService {
  constructor(
    private _httpClient: HttpClient,
    private _oktaAuthStateService: OktaAuthStateService,
  ) {}

  private _principal$: Observable<IPrincipal> | undefined;

  /**
   * Attempts to get the Principal object
   */
  public get principal$(): Observable<IPrincipal> {
    if (!this._principal$) {
      this._principal$ = this._fetchPrincipal();
    }
    return this._principal$;
  }

  private _fetchPrincipal(): Observable<IPrincipal> {
    return this._oktaAuthStateService.authState$.pipe(
      filter((authState: AuthState) => !!authState.accessToken?.accessToken),
      switchMap(() =>
        this._httpClient
          .get<IPrincipal>(PRINCIPAL_RESOURCE_URL, {
            withCredentials: true,
          })
          .pipe(shareReplay(1)),
      ),
    );
  }
}
